/**
 * Os textos do cartão "Avisos com o … fechado" (passo 8, PR 2; D3, PR 2). O estado (iPhone fora da Tela de Início → sem suporte →
 * bloqueado → erro → em outro app → ligado → desligado) é o mesmo nos dois apps; o que muda é o NOME do app e a quem pedir ajuda.
 *
 * Os do interno são os de sempre, palavra por palavra. Os do portal são do desenho do portal §5.4 — não mudam sem passar pelo
 * Augusto: "o portal" no lugar de "SE_WEB", "Ponto Banese" no ícone do iPhone, "fale com a equipe pelo Atendimento" no lugar de
 * "avise a TI" (o parceiro não tem TI; e nunca vê a marca interna).
 *
 * `emOutroApp` e `receberAqui` (decisão do Augusto, 12/10/2026: o push só troca de dono num clique) foram aprovados por ele, palavra
 * por palavra. O do portal diz "outro acesso" de propósito: o parceiro não sabe que o SE_WEB existe.
 *
 * `aviso` (o aviso flutuante de quando o push não volta sozinho, plano "Push: religar sozinho ou avisar", Task 8) foi aprovado pelo
 * Augusto em 29/10/2026: as frases do interno são as do plano, e as do portal são as mesmas com "o portal" no lugar de "o SE_WEB".
 */

/** Frase repetida nos estados desligado e ligado (mesmos nomes de nível do `TEXTO_DOS_NIVEIS`). */
export const SO_SOM_E_AVISO = "Só para os assuntos marcados como “Aviso + som” ou “Aviso sem som”.";

/** Os textos do aviso flutuante (`AvisoDoPush.tsx`): "religar" (permissão voltou a perguntar, ou o religar sozinho não deu) e "desbloquear". */
export interface TextosDoAvisoDoPush {
  religarTitulo: string;
  /** Corpo do aviso `permissao-voltou-a-perguntar`. */
  religarCorpoPermissao: string;
  /** Corpo do aviso `precisa-de-clique` (não há inscrição e a permissão está dada). */
  religarCorpoSemInscricao: string;
  religarBotao: string;
  /** O botão que cala o aviso de religar por 7 dias. */
  agoraNao: string;
  desbloquearTitulo: string;
  /** A frase antes dos passos (o aviso não repete a `bloqueado.intro` do cartão). */
  desbloquearIntro: string;
  /** O botão que cala o aviso de desbloquear por 30 dias. */
  entendi: string;
}

export interface TextosDoPush {
  titulo: string;
  ios: { intro: string; passoAbrir: string };
  semSuporte: string;
  bloqueado: { intro: string; rodape: string };
  orientacaoDoErro: string;
  ligado: string;
  /** A frase antes de `SO_SOM_E_AVISO` no estado desligado. */
  desligado: string;
  /** Há inscrição no navegador, mas o push do aparelho está com o OUTRO app (os avisos não chegam aqui). */
  emOutroApp: string;
  /** O botão desse estado: toma o push para este app. */
  receberAqui: string;
  /** O aviso flutuante de quando o push não volta sozinho. */
  aviso: TextosDoAvisoDoPush;
}

export const TEXTOS_DO_PUSH_INTERNO: TextosDoPush = {
  titulo: "Avisos com o SE_WEB fechado",
  ios: {
    intro:
      "No iPhone e no iPad, esses avisos só chegam com o SE_WEB instalado na Tela de Início (iOS 16.4 ou mais novo). Para instalar:",
    passoAbrir: "Abra o SE_WEB pelo ícone novo e ligue os avisos nesta tela.",
  },
  semSuporte:
    "Este navegador não aceita avisos com o SE_WEB fechado. Os avisos continuam no sino. No Chrome, no Edge ou no Firefox, dá para ligar.",
  bloqueado: {
    intro: "O navegador está bloqueando os avisos do SE_WEB. Para desbloquear:",
    rodape: "No SE_WEB instalado no iPhone, é em Ajustes → Notificações → SE_WEB.",
  },
  orientacaoDoErro: "Tente de novo. Se continuar, avise a TI.",
  ligado: "Avisos com o SE_WEB fechado chegam neste aparelho.",
  desligado:
    "Receba os avisos neste celular ou computador mesmo com o SE_WEB fechado ou atrás de outra aba.",
  emOutroApp: "Neste aparelho, os avisos estão indo para o Portal do Parceiro.",
  receberAqui: "Receber aqui",
  aviso: {
    religarTitulo: "Os avisos com o SE_WEB fechado pararam neste aparelho",
    religarCorpoPermissao:
      "O navegador voltou a perguntar se o SE_WEB pode mostrar notificações. Ligue de novo para receber os avisos mesmo com o SE_WEB fechado.",
    religarCorpoSemInscricao:
      "Os avisos deixaram de chegar com o SE_WEB fechado. Ligue de novo para voltar a recebê-los.",
    religarBotao: "Ligar de novo",
    agoraNao: "Agora não",
    desbloquearTitulo: "Os avisos com o SE_WEB fechado estão bloqueados no navegador",
    desbloquearIntro: "Para voltar a recebê-los, desbloqueie:",
    entendi: "Entendi",
  },
};

export const TEXTOS_DO_PUSH_DO_PORTAL: TextosDoPush = {
  titulo: "Avisos com o portal fechado",
  ios: {
    intro:
      "No iPhone e no iPad, esses avisos só chegam com o portal instalado na Tela de Início (iOS 16.4 ou mais novo). Para instalar:",
    passoAbrir: "Abra o portal pelo ícone novo (Ponto Banese) e ligue os avisos nesta tela.",
  },
  semSuporte:
    "Este navegador não aceita avisos com o portal fechado. Os avisos continuam no sino. No Chrome, no Edge ou no Firefox, dá para ligar.",
  bloqueado: {
    intro: "O navegador está bloqueando os avisos do portal. Para desbloquear:",
    rodape: "No portal instalado no iPhone, é em Ajustes → Notificações → Ponto Banese.",
  },
  orientacaoDoErro: "Tente de novo. Se continuar, fale com a equipe pelo Atendimento.",
  ligado: "Avisos com o portal fechado chegam neste aparelho.",
  desligado:
    "Receba os avisos neste celular ou computador mesmo com o portal fechado ou atrás de outra aba.",
  emOutroApp: "Neste aparelho, os avisos estão indo para outro acesso.",
  receberAqui: "Receber aqui",
  aviso: {
    religarTitulo: "Os avisos com o portal fechado pararam neste aparelho",
    religarCorpoPermissao:
      "O navegador voltou a perguntar se o portal pode mostrar notificações. Ligue de novo para receber os avisos mesmo com o portal fechado.",
    religarCorpoSemInscricao:
      "Os avisos deixaram de chegar com o portal fechado. Ligue de novo para voltar a recebê-los.",
    religarBotao: "Ligar de novo",
    agoraNao: "Agora não",
    desbloquearTitulo: "Os avisos com o portal fechado estão bloqueados no navegador",
    desbloquearIntro: "Para voltar a recebê-los, desbloqueie:",
    entendi: "Entendi",
  },
};
